'use client';

import { useMemo } from 'react';

import {
  formatKgCo2e,
  toResolvedCandidateFromManualFactor,
} from '../lib/activity-form-helpers';
import type { EmissionFactor } from '../types';

import { ActivityFormFactorBadge } from './ActivityFormFactorBadge';

import { Button } from '@/components/ui/button';
type ActivityAssignmentFactorOverrideSummaryProps =
  | {
      kind: 'factor';
      factorPreview: EmissionFactor;
      selectorSource?: 'climatiq' | 'custom';
      onClear?: () => void;
      clearLabel?: string;
      disabled?: boolean;
    }
  | {
      kind: 'direct';
      manualFactorKgCo2ePerUnit: string;
      onClear?: () => void;
      clearLabel?: string;
      disabled?: boolean;
    };

export function ActivityAssignmentFactorOverrideSummary({
  onClear,
  disabled = false,
  ...override
}: ActivityAssignmentFactorOverrideSummaryProps) {
  const resolvedCandidate = useMemo(
    () =>
      override.kind !== 'factor'
        ? undefined
        : toResolvedCandidateFromManualFactor(
            {
              id: override.factorPreview.id,
              activityId: override.factorPreview.activityId,
              name: override.factorPreview.name,
              source: override.factorPreview.source,
              region: override.factorPreview.region,
              regionName: override.factorPreview.regionName,
              year: override.factorPreview.year,
              unit: override.factorPreview.unit,
              unitType: override.factorPreview.unitType,
              co2ePerUnit: override.factorPreview.co2ePerUnit,
            },
            override.factorPreview.selectorSource === 'custom'
              ? 'custom'
              : override.factorPreview.sourceDataset ||
                  override.factorPreview.source ||
                  'selected override'
          ),
    [override]
  );

  const effectiveSelectorSource =
    override.kind === 'factor'
      ? override.selectorSource ?? override.factorPreview.selectorSource ?? 'climatiq'
      : null;

  return (
    <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50/60 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-amber-900">
          Emission factor override
        </p>
        {onClear ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={onClear}
            disabled={disabled}
          >
            {override.clearLabel ?? 'Use Suggested Factor'}
          </Button>
        ) : null}
      </div>
      {override.kind === 'factor' ? (
        <ActivityFormFactorBadge
          factor={resolvedCandidate}
          mode="manual"
          reason={`Selected manually from ${effectiveSelectorSource ?? 'unknown'} factor search results.`}
          disabled={disabled}
        />
      ) : (
        <div className="rounded-md border border-amber-200/80 bg-background/80 px-3 py-2 text-sm">
          <p className="font-medium text-foreground">
            {formatKgCo2e(Number(override.manualFactorKgCo2ePerUnit))}/unit
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Direct manual factor entered in the override dialog.
          </p>
        </div>
      )}
    </div>
  );
}
